import dialogService from 'DialogService';
import global from 'Global';
import materialDesignVueDialogService from 'MaterialDesignVueDialogService';
import Promise from 'bluebird';

class MaterialDesignDialogService {
	constructor() {
		this._uniqueDialogsMap = new Map();
	}

	registerLoader(loader) {
		this.loader = loader;
	}

	unregisterLoader() {
		this.hideAllDialogs();
		delete this.loader;
	}

	canShowDialogs() {
		return this.loader && global.materialDesign && !dialogService.isDialogOpen();
	}

	loadDialogWithPropsAsync(component, { props, on }) {
		function showDialogAsync() {
			props.value = true;
			return new Promise(() => {});
		}

		if (this.loader) {
			return this.loader.loadDialogWithPropsAsync(component, { props, on, showDialogAsync });
		}
		return Promise.reject();
	}

	loadDialogAsync(component, extender) {
		if (this.loader) {
			return this.loader.loadDialogAsync(component, extender);
		}
		return Promise.reject();
	}

	loadDialogWithComponentDefinition(component, { showDialogAsync, hideDialog }) {
		if (this.loader) {
			const on = {
				input: (value) => {
					if (!value) {
						hideDialog();
					}
				}
			};

			return this.loader.loadDialogWithComponentDefinition(component, { props: {}, on, showDialogAsync, hideDialog });
		}

		return null;
	}

	hideAllDialogs() {
		materialDesignVueDialogService.hideAllDialogs();
		if (this.loader) {
			return this.loader.hideAllDialogs();
		}
	}

	canShowAlertDialog() {
		return this.canShowDialogs();
	}

	async showAlertDialogAsync(
		title,
		message,
		buttons,
		messageType,
		uniqueDialogID,
		messageResultType,
		customMessage
	) {
		const dialogKey = uniqueDialogID?.trim();
		if (!dialogKey) {
			return await getNewDialogPromiseAsync(this);
		}

		if (!this._uniqueDialogsMap.has(dialogKey)) {
			this._uniqueDialogsMap.set(dialogKey, getNewDialogPromiseAsync(this));
		}
		return await this._uniqueDialogsMap.get(dialogKey);

		async function getNewDialogPromiseAsync(self) {
			/*! SuppressStringValidation component/extender name */
			const dialog = await self.loadDialogAsync('GlowAlertDialog', 'AlertDialogExtender');
			const result = await dialog.showDialogAsync(
				title,
				message,
				buttons,
				messageType,
				messageResultType,
				customMessage
			);
			self._uniqueDialogsMap.delete(dialogKey);
			return result;
		}
	}

	canShowDocumentDialog() {
		return Boolean(global.featureFlags.enableGridSupplyDialogs && this.canShowDialogs());
	}

	async showDocumentDialogAsync(documentName, documentViewModel) {
		/*! SuppressStringValidation component/extender name */
		const dialog = await this.loadDialogAsync('DocumentDialog', 'DocumentDialogExtender');
		return await dialog.showDialogAsync(documentName, documentViewModel);
	}

	canShowCustomizeDocumentsDialog() {
		return Boolean(global.featureFlags.enableCustomizeDocuments && this.canShowDialogs());
	}

	async showCustomizeDocumentsDialogAsync() {
		/*! SuppressStringValidation component/extender name */
		const dialog = await this.loadDialogAsync(
			'CustomizeDocumentsDialog',
			'CustomizeDocumentsDialogExtender'
		);
		return await dialog.showDialogAsync();
	}

	canShowChangePasswordDialog() {
		return this.canShowDialogs();
	}

	async showChangePasswordDialogAsync(oldPassword) {
		/*! SuppressStringValidation component/extender name */
		const dialog = await this.loadDialogAsync(
			'ChangePasswordDialog',
			'ChangePasswordDialogExtender'
		);
		return await dialog.showDialogAsync(oldPassword);
	}

	canShowChangeBranchDepartmentDialog() {
		return this.canShowDialogs();
	}

	async showChangeBranchDepartmentDialogAsync(viewModel) {
		/*! SuppressStringValidation component/extender name */
		const dialog = await this.loadDialogAsync(
			'ChangeBranchDepartmentDialog',
			'ChangeBranchDepartmentDialogExtender'
		);
		return await dialog.showDialogAsync(viewModel);
	}

	async showImpersonateUserDialogAsync() {
		/*! SuppressStringValidation component/extender name */
		const dialog = await this.loadDialogAsync(
			'ImpersonateUserDialog',
			'ImpersonateUserDialogExtender'
		);
		return await dialog.showDialogAsync();
	}

	canShowErrorDialog() {
		return this.canShowDialogs();
	}

	async showErrorDialogAsync(
		message,
		title,
		notificationSummary,
		buttonOptions,
		filter
	) {
		/*! SuppressStringValidation component/extender name */
		const dialog = await this.loadDialogAsync('ErrorDialog', 'ErrorDialogExtender');
		return await dialog.showDialogAsync(
			message,
			title,
			notificationSummary,
			buttonOptions,
			filter
		);
	}

	canShowErrorReportingDialog() {
		return this.canShowDialogs();
	}

	async showErrorReportingDialogAsync(errorDetails, environment, errorID) {
		/*! SuppressStringValidation component/extender name */
		const dialog = await this.loadDialogAsync(
			'ErrorReportingDialog',
			'ErrorReportingDialogExtender'
		);
		return await dialog.showDialogAsync(errorDetails, environment, errorID);
	}

	canShowThemeConfigurationDialog() {
		return this.canShowDialogs();
	}

	async showThemeConfigurationDialogAsync() {
		/*! SuppressStringValidation component/extender name */
		const dialog = await this.loadDialogAsync(
			'ThemeConfigurationDialog',
			'ThemeConfigurationDialogExtender'
		);
		return await dialog.showDialogAsync();
	}

	async showEditThemeConfigurationDialogAsync(displayMode, template) {
		/*! SuppressStringValidation component/extender name */
		const dialog = await this.loadDialogAsync(
			'ThemeConfigurationDialog',
			'ThemeConfigurationDialogExtender'
		);
		return await dialog.showDialogAsync(displayMode, template);
	}

	async showThemeManagementDialogAsync() {
		/*! SuppressStringValidation component/extender name */
		const dialog = await this.loadDialogAsync('ThemeManagementDialog', 'ThemeManagementDialogExtender');
		return await dialog.showDialogAsync();
	}

	canShowConversationDialog() {
		return this.canShowDialogs();
	}

	async showConversationDialogAsync(entity, bindingPath, readonly) {
		/*! SuppressStringValidation component/extender name */
		const dialog = await this.loadDialogAsync(
			'ConversationDialog',
			'ConversationDialogExtender'
		);
		return await dialog.showDialogAsync(entity, bindingPath, readonly);
	}

	canShowMessageDialog() {
		return this.canShowDialogs();
	}

	async showMessageDialogAsync(entity, title) {
		/*! SuppressStringValidation component/extender name */
		const dialog = await this.loadDialogAsync('MessageDialog', 'MessageDialogExtender');
		return await dialog.showDialogAsync(entity, title);
	}

	canShowEDocsDialog() {
		return this.canShowDialogs();
	}

	async showEDocsDialogAsync(entity) {
		/*! SuppressStringValidation component/extender name */
		const dialog = await this.loadDialogAsync('EDocsDialog', 'EDocsDialogExtender');
		return await dialog.showDialogAsync(entity);
	}

	canShowProgressDialog() {
		return this.canShowDialogs();
	}

	async showProgressDialogAsync(caption) {
		/*! SuppressStringValidation component/extender name */
		const dialog = await this.loadDialogAsync('WtgProgressDialog', 'ProgressDialogExtender');
		dialog.showDialogAsync(caption);
		return { hide: dialog.hideDialog };
	}

	canShowPromptForValueDialog() {
		return this.canShowDialogs();
	}

	async showPromptForValueDialogAsync(title, message, buttons, messageType) {
		/*! SuppressStringValidation component/extender name */
		const dialog = await this.loadDialogAsync(
			'GlowPromptForValueDialog',
			'PromptForValueDialogExtender'
		);
		return await dialog.showDialogAsync(title, message, buttons, messageType);
	}

	canShowColorSchemesDialog() {
		return this.canShowDialogs();
	}

	async showColorSchemeManageDialogAsync(props, on) {
		/*! SuppressStringValidation component name */
		const dialog = await this.loadDialogWithPropsAsync('WtgManageColorSchemesDialog', {
			props,
			on,
		});
		dialog.showDialogAsync();
		return { hide: dialog.hideDialog };
	}

	async showColorSchemeEditDialogAsync(props, on) {
		/*! SuppressStringValidation component name */
		const dialog = await this.loadDialogWithPropsAsync('WtgEditColorSchemeDialog', {
			props,
			on,
		});
		dialog.showDialogAsync();
		return { hide: dialog.hideDialog };
	}

	canShowAdvancedSearchDialog() {
		return this.canShowDialogs();
	}

	async showAdvancedSearchDialogAsync() {
		/*! SuppressStringValidation component/extender name */
		const dialog = await this.loadDialogAsync('AdvancedSearchDialog', 'AdvancedSearchDialogExtender');
		return await dialog.showDialogAsync.apply(null, arguments);
	}

}

export default new MaterialDesignDialogService();
